(<template>
  <header class="b-header">
    <span v-if="showUserCountry"
          class="b-header__country-test">{{ userCountry }}</span>
    <div class="b-header__logo-print"></div>
    <router-link v-if="!showBackBtn"
                 :to="$makeRoute({name: 'BuyerHome'})"
                 class="b-header__logo" />
    <button v-if="showBackBtn"
            class="b-header__back-btn"
            @click="historyBack">{{ $gettext('Back') }}</button>
    <router-link :to="$makeRoute({name: 'BuyerHome'})"
                 class="b-header__usual-link b-header__enterprise-link b-header__usual-link--first">{{ $gettext('Home') }}</router-link>
    <header-nav-dropdown v-if="!isTranslationOnly"
                         :dropdown-subtitle="interpretationDropdownSubtitle"
                         :links="interpretationDropdownLinks"
                         class="header-desktop-nav" />
    <header-nav-dropdown :dropdown-subtitle="translationDropdownSubtitle"
                         :links="translationDropdownLinks"
                         class="header-desktop-nav" />
    <book-menu-dropdown />
    <router-link v-if="!userIsEnterpriseMember"
                 :to="$makeRoute({name: 'BuyerUpgradeProfile'})"
                 class="sk-btn sk-btn--white b-header__upgrade-btn"
                 v-html="upgradeText" />
    <div class="b-header__account-cont">
      <p v-if="$isGodmode()"
         class="sk-btn sk-btn--white b-header__godmode-btn">{{ $gettext('Admin mode') }}</p>
      <language-selector v-if="!isMobileView" />
      <a :href="'https://support.salita.no/collection/380-tolkebruker'"
         target="_blank"
         class="b-header__usual-link help-link">{{ $gettext('Help') }}</a>
      <button ref="accountBtn"
              :class="{'header__account-btn--active': subnavShown}"
              class="header__account-btn"
              :aria-label="openAccountMenuText"
              @click.stop="toggleSubnav">
        <tt-avatar :own="true"
                   :class="{'b-header__nav-avatar--offset': userIsEnterpriseMember}"
                   class="b-header__account-avatar" />
        <company-avatar v-if="userIsEnterpriseMember"
                        :own="true"
                        class="b-header__account-logo" />
      </button>
      <header-account-subnav v-if="subnavShown" />
    </div>
  </header>
</template>)

<script>
  import {mapGetters} from 'vuex';
  import LanguageSelector from '@/components/dashboard_components/LanguageSelector';
  import BookMenuDropdown from '@/components/dashboard_components/BookMenuDropdown';
  import HeaderNavDropdown from '@/components/shared_components/HeaderNavDropdown';
  import HeaderAccountSubnav from '@/components/navigations/header_account_subnav/HeaderAccountSubnav';

  const isBrowser = new Function('try {return this===window;}catch(e){ return false;}');

  export default {
    components: {
      'language-selector': LanguageSelector,
      'book-menu-dropdown': BookMenuDropdown,
      'header-nav-dropdown': HeaderNavDropdown,
      'header-account-subnav': HeaderAccountSubnav
    },
    data() {
      return {
        openedSubnav: false,
        clientWidth: 768,
        showAdvert: !this.$isGodmode() && this.$store.getters['UserInfoStore/showAdvertisement']
      };
    },
    computed: {
      ...mapGetters('UserInfoStore', [
        'userUid',
        'userIsEnterpriseMember',
        'userCanShowStatistics',
        'isEmailVerificationRequired',
        'showJobStatistics',
        'userIsAdminOfEnterprise',
        'userCanSeeTranslations',
        'disableOtherServices',
        'isTranslationOnly'
      ]),
      userCountry() { return this.$store.state.userCountry || 'countryNotDetected'; },
      showUserCountry() {
        // enable this cookie to show Business country
        if (isBrowser()) {
          return this.$cookieManager.getCookie('showUserCountry');
        }
      },
      interpretationDropdownLinks() {
        return [
          {
            text: this.$gettext('Interpretation assignments'),
            type: 'router-link',
            iconClass: 'interpretation-assignments-icon',
            action: this.assignmentsLink,
            condition: true
          },
          {
            text: this.$gettext('Calendar'),
            type: 'router-link',
            iconClass: 'calendar-icon',
            action: this.calendarLink,
            condition: true
          },
          {
            text: this.$gettext('Interpretation statistics'),
            type: 'router-link',
            iconClass: 'interpretation-statistics-icon',
            action: this.interpretationStatisticsLink,
            condition: this.userCanShowStatistics
          },
          {
            text: this.$gettext('Favorite interpreters'),
            type: 'router-link',
            iconClass: 'favorite-interpreters-icon',
            action: this.interpretersLink,
            condition: true
          },
          {
            text: this.userIsEnterpriseMember ? this.$gettext('Book specific interpreter') : this.$gettext('Book interpreter'),
            type: 'router-link',
            iconClass: 'book-specific-interpreter-icon',
            action: this.capacityLink,
            condition: !this.isEmailVerificationRequired
          }
        ];
      },
      translationDropdownLinks() {
        return [
          {
            text: this.$gettext('Translation assignments'),
            type: 'router-link',
            iconClass: 'translation-assignments-icon',
            action: this.$makeRoute({name: 'BuyerAllProjects'}),
            condition: true
          },
          {
            text: this.$gettext('Translation statistics'),
            type: 'router-link',
            iconClass: 'translation-statistics-icon',
            action: this.translationStatisticsLink,
            condition: this.userCanShowStatistics
          }
        ];
      },
      interpretationStatisticsLink() {
        return this.showJobStatistics
          ? this.$makeRoute({name: 'BuyerInterpretationStatistics'})
          : this.$makeRoute({name: 'BuyerStatisticsChart', query: {view: 'interpretation'}, params: {chartId: 'line_type'}});
      },
      translationStatisticsLink() {
        return this.$makeRoute({name: 'BuyerStatisticsChart', query: {view: 'translation'}, params: {chartId: 'line_type'}});
      },
      calendarLink() {
        return this.$makeRoute({
          name: 'BuyerCalendar',
          query: {
            view: 'week',
            date: this.$moment().format('YYYY-MM-DD')
          }
        });
      },
      capacityLink() {
        return this.$makeRoute({
          name: 'BuyerCapacity',
          query: {
            view: 'day',
            date: this.$moment().format('YYYY-MM-DD'),
            page: 1
          }
        });
      },
      homeLink() {
        return this.$makeRoute({
          name: 'BuyerHome'
        });
      },
      assignmentsLink() {
        return this.$makeRoute({
          name: 'BuyerAllAssignments',
          query: {
            view: 'current',
            sort: 1,
            page: 1
          }
        });
      },
      interpretersLink() {
        return this.$makeRoute({
          name: 'BuyerInterpreters',
          query: {
            page: 1,
            availability_date: this.$moment().format('YYYY-MM-DD'),
            langFrom: '3'
          }
        });
      },
      subnavShown() {
        return this.openedSubnav;
      },
      showBackBtn() {
        return this.$store.state.subPage && this.$store.state.GlobalStore.touchScreen;
      },
      upgradeText() { return this.$gettext('Upgrade to<br/>enterprise'); },
      isMobileView() { return this.$store.state.GlobalStore.clientWidth < 768; },
      interpretationDropdownSubtitle() { return this.$gettext('Interpretation'); },
      translationDropdownSubtitle() { return this.$pgettext('translate', 'Translation'); },
      openAccountMenuText() { return this.$gettext('Open account menu'); }
    },
    methods: {
      closeModal() {
        this.$emit('closemodal');
      },
      toggleSubnav(e) {
        this.$store.commit('SelectsStore/closeOpenedSelect');
        this.toggleAbsoluteBlocks();
        this.openedSubnav = !this.openedSubnav;
        if (e.target.className.indexOf('b-header__account-') != -1) {
          this.$refs.accountBtn.blur();
        }
      },
      toggleAbsoluteBlocks(e) {
        if (e && (e.target.className.indexOf('b-header__account-') != -1 && e.target.className.indexOf('b-header__notifications-') != -1)) {
          return;
        }
        if (e && e.target.className.indexOf('language-dropdown') != -1) {
          return;
        }
        this.openedSubnav = false;
      },
      setClientWidth() {
        this.clientWidth = document.documentElement.clientWidth;
      },
      historyBack() {
        if (document.referrer && document.referrer.indexOf('sms') != -1) {
          this.$router.push('/');
        } else {
          const path = this.$route.fullPath;
          this.$router.back();
          setTimeout(() => {
            if (this.$route.fullPath == path) this.$router.push('/');
          }, 500);
        }
      },
      closeAdvertisement() {
        this.showAdvert = false;
      }
    },
    mounted() {
      this.setClientWidth();
      document.addEventListener('click', this.toggleAbsoluteBlocks);
      window.addEventListener('resize', this.setClientWidth);
    }
  };
</script>
